import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Search, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { CustomerPicker } from "@/components/CustomerPicker";
import { toast } from "sonner";

export default function VehicleNew() {
  const navigate = useNavigate();
  const [vrm, setVrm] = useState("");
  const [looking, setLooking] = useState(false);
  const [looked, setLooked] = useState(false);
  const [form, setForm] = useState({ make: "", model: "", year: "", colour: "", fuel_type: "", engine_capacity: "", mot_expiry: "", tax_expiry: "" });
  const [customer, setCustomer] = useState(null);
  const [saving, setSaving] = useState(false);

  const lookup = async () => {
    if (!vrm.trim()) return;
    setLooking(true);
    try {
      const d = await api(`/dvla/lookup/${encodeURIComponent(vrm.trim())}`);
      setForm({
        make: d.make || "", model: d.model || "", year: d.year || "", colour: d.colour || "",
        fuel_type: d.fuel_type || "", engine_capacity: d.engine_capacity || "", mot_expiry: d.mot_expiry || "", tax_expiry: d.tax_expiry || "",
      });
      setLooked(true);
      toast.success("Vehicle found on DVLA");
    } catch (e) { toast.error(e.message || "Vehicle not found"); }
    finally { setLooking(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!vrm.trim() || !form.make || !form.model) { toast.error("VRM, make and model are required"); return; }
    setSaving(true);
    try {
      const v = await api("/vehicles", { method: "POST", body: JSON.stringify({
        vrm: vrm.trim(), make: form.make, model: form.model,
        year: form.year ? Number(form.year) : null, colour: form.colour || null, fuel_type: form.fuel_type || null,
        engine_capacity: form.engine_capacity ? Number(form.engine_capacity) : null,
        mot_expiry: form.mot_expiry || null, tax_expiry: form.tax_expiry || null,
        customer_id: customer?.id || null,
      }) });
      toast.success("Vehicle added");
      navigate(`/vehicles/${v.id}`);
    } catch (e2) { toast.error(e2.message || "Could not add vehicle"); }
    finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-xl">
      <Link to="/vehicles" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-2"><ArrowLeft className="h-4 w-4" />Vehicles</Link>
      <PageHeader title="Add Vehicle" />
      <form onSubmit={submit} className="space-y-5 rounded-xl border border-border bg-card p-6">
        <div>
          <span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">REGISTRATION (VRM) *</span>
          <div className="flex gap-2">
            <input value={vrm} onChange={(e) => setVrm(e.target.value.toUpperCase())} placeholder="AB12 XYZ" data-testid="vrm-input"
              className="flex-1 rounded-lg border border-input bg-plate px-3.5 py-3 text-center font-mono text-xl font-bold uppercase tracking-wider text-black outline-none focus:ring-2 focus:ring-brand/40" />
            <button type="button" onClick={lookup} disabled={looking} data-testid="dvla-lookup-btn" className="inline-flex items-center gap-2 rounded-lg bg-midnight px-4 py-3 text-sm font-bold text-white disabled:opacity-60">
              {looking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}Lookup
            </button>
          </div>
          {looked && <p className="mt-1.5 flex items-center gap-1 text-xs font-semibold text-success"><CheckCircle2 className="h-3.5 w-3.5" />DVLA details loaded — review below</p>}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <F label="MAKE *" v={form.make} on={(v) => setForm({ ...form, make: v })} t="veh-make" />
          <F label="MODEL *" v={form.model} on={(v) => setForm({ ...form, model: v })} t="veh-model" />
          <F label="YEAR" v={form.year} on={(v) => setForm({ ...form, year: v })} t="veh-year" />
          <F label="COLOUR" v={form.colour} on={(v) => setForm({ ...form, colour: v })} t="veh-colour" />
          <F label="FUEL" v={form.fuel_type} on={(v) => setForm({ ...form, fuel_type: v })} t="veh-fuel" />
          <F label="ENGINE (CC)" v={form.engine_capacity} on={(v) => setForm({ ...form, engine_capacity: v })} t="veh-engine" />
          <F label="MOT EXPIRY" v={form.mot_expiry} on={(v) => setForm({ ...form, mot_expiry: v })} t="veh-mot" placeholder="YYYY-MM-DD" />
          <F label="TAX DUE" v={form.tax_expiry} on={(v) => setForm({ ...form, tax_expiry: v })} t="veh-tax" placeholder="YYYY-MM-DD" />
        </div>

        <div>
          <span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">CUSTOMER (OPTIONAL)</span>
          <CustomerPicker value={customer} onChange={setCustomer} />
        </div>

        <button type="submit" disabled={saving} data-testid="veh-save-btn" className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-3.5 text-sm font-extrabold tracking-wide text-white disabled:opacity-60">
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "ADD VEHICLE"}
        </button>
      </form>
    </div>
  );
}

function F({ label, v, on, t, placeholder }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">{label}</span>
      <input data-testid={t} value={v} onChange={(e) => on(e.target.value)} placeholder={placeholder}
        className="w-full rounded-lg border border-input bg-card px-3.5 py-2.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
    </label>
  );
}
